import React, { useEffect, useState, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import CloudProvidersCapture from 'Location/CloudConnect/CloudProviders/CloudProvidersCapture';
import { ICloudProvider } from 'Location/CloudConnect/CloudProviders/types';
import Dropdown, { IDropdownOption } from 'shared/components/molecules/Dropdown';
import { BearerType } from 'Quotes/QuoteBuilder/components/Configure/Bearer/BearerType';
import { cloudProviderForDisplay } from 'Order/OrderBuilder/shared/utils/cloudProviderForDisplay';

const Container = styled.div`
  margin-top: 1.5em;
  margin-bottom: -15px;
`;

interface CloudAddOnProps {
  customerId?: string;
  bearer?: BearerType;
  usedBandwidth?: string | null;
  availableProviders?: ICloudProvider[] | null;
  selectedProviderName?: string | null;
  selectedBandwidth?: string | null;
  readonly: boolean;
  onChange?: (providerId: string | null, bandwidth: string | null) => void;
}

const CloudAddOn: React.FC<CloudAddOnProps> = ({
  customerId,
  bearer,
  usedBandwidth,
  availableProviders,
  selectedProviderName,
  selectedBandwidth,
  readonly,
  onChange,
}) => {
  const [providers, setProviders] = useState<ICloudProvider[]>([]);
  const [providerName, setProviderName] = useState<string | null>(selectedProviderName ?? null);
  const [bandwidth, setBandwidth] = useState<string | null>(selectedBandwidth ?? null);

  const maxBandwidth = bearer?.replace('Port_', '').replace('Mbits', '');
  const availableBandwidth = parseInt(maxBandwidth ?? "0", 10) - parseInt(usedBandwidth ?? "0", 10);

  useEffect(() => {
    const usableProviders = availableProviders?.filter(
      (item) =>
        item.attributes.is_enabled &&
        Array.isArray(item.attributes.supported_bandwidths) &&
        item.attributes.supported_bandwidths.length > 0 &&
        item.attributes.supported_bandwidths.some(
          (bw) => bw <= availableBandwidth
        )
    );
    setProviders(usableProviders ?? []);
  }, [availableProviders, availableBandwidth]);

  // Sync local state with props
  useEffect(() => setProviderName(selectedProviderName ?? null), [selectedProviderName]);
  useEffect(() => setBandwidth(selectedBandwidth ?? null), [selectedBandwidth]);

  // Notify parent on change, but only after initial mount
  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) {
      onChange?.(providerName, bandwidth);
    } else {
      didMount.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerName, bandwidth]);

  const selectedProvider = providers.find((p) => p.attributes.name === providerName);

  const bandwidthOptions: IDropdownOption[] = selectedProvider
    ? selectedProvider.attributes.supported_bandwidths
        .filter((bw) => bw <= availableBandwidth)
        .map((bw) => ({
          value: String(bw),
          label: bw >= 1000 ? `${bw / 1000} Gbps` : `${bw} Mbps`,
        }))
    : [];

  const GlobalStyle = createGlobalStyle`
    .image-button {
      margin: 0 15px 15px 0 !important;
    }
  `;

  // Hide entire component if no providers or no bandwidth options when provider is selected
  if (providers.length === 0 || (providerName && selectedProvider && bandwidthOptions.length === 0)) {
    return null;
  }

  return (
    <Container>
      <GlobalStyle />

      <div className="mt-4 mb-2 addon_options__title">Do you require a Cloud Connect connection?</div>
      <p className='text-muted mb-4'>One access tail that provides both public internet & private cloud connectivity.</p>

      <CloudProvidersCapture
        cloudProvidersList={providers}
        selectedProviderName={selectedProvider?.attributes.name}
        onCloudProviderSelected={(provider: ICloudProvider) => {
          setProviderName(selectedProvider?.attributes.name === provider.attributes.name ? null : provider.attributes.name);
          setBandwidth(null);
        }}
        customerId={customerId}
        headerText=""
        readonly={readonly}
        footerText={selectedProvider ? (`Based on the selected bearer and circuit, the maximum available bandwidth for ${cloudProviderForDisplay(selectedProvider?.attributes.name)} is ${bandwidthOptions[bandwidthOptions.length - 1]?.label}`) : ''}
      />

      {providerName && selectedProvider && bandwidthOptions.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1em' }}>
        <div style={{ marginRight: '0.35em' }}>Bandwidth size</div>
        <div style={{ flexGrow: 1, paddingRight: '3em' }}>
          <Dropdown
            options={bandwidthOptions}
            value={bandwidthOptions.find((opt) => String(opt.value) === bandwidth) || null}
            isDisabled={readonly}
            placeholder="Select bandwidth"
            onChange={(option) => setBandwidth(option ? String(option.value) : null)}
          />
        </div>
      </div>
      )}
    </Container>
  );
};

export default CloudAddOn;